import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Badge from "@mui/material/Badge"
import List from "@mui/material/List"
import ListItemButton from "@mui/material/ListItemButton"
import ListItemText from "@mui/material/ListItemText"
import Paper from "@mui/material/Paper"
import TextField from "@mui/material/TextField"
import ToggleButton from "@mui/material/ToggleButton"
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup"
import Typography from "@mui/material/Typography"
import { useCallback, useEffect, useState } from "react"

import { useAuthorizedAxiosClient } from "../../common/axios"
import Column from "../../common/components/Column"
import Row from "../../common/components/Row"
import { apiErrorMessage, clientAndCampaign } from "./podDialer"

const POLL_MS = 10000

/** The pod's shared texting inbox; replies route to the agent who last contacted the lead. */
export default function PodInboxPanel() {
  const axios = useAuthorizedAxiosClient()
  const [scope, setScope] = useState("mine")
  const [threads, setThreads] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [thread, setThread] = useState(null)
  const [reply, setReply] = useState("")
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState("")

  const loadThreads = useCallback(
    () =>
      axios
        .get("/pods/api/agent/inbox/threads/", { params: { scope } })
        .then(({ data }) => setThreads(data.results || []))
        .catch(() => setError("Could not load texts.")),
    [axios, scope]
  )

  const loadThread = useCallback(
    (id) =>
      axios
        .get(`/pods/api/agent/inbox/threads/${id}/`)
        .then(({ data }) => setThread(data))
        .catch(() => setError("Could not load the conversation.")),
    [axios]
  )

  useEffect(() => {
    loadThreads()
    const timer = window.setInterval(loadThreads, POLL_MS)
    return () => window.clearInterval(timer)
  }, [loadThreads])

  useEffect(() => {
    if (!selectedId) {
      setThread(null)
      return undefined
    }
    loadThread(selectedId)
    const timer = window.setInterval(() => loadThread(selectedId), POLL_MS)
    return () => window.clearInterval(timer)
  }, [loadThread, selectedId])

  function sendReply() {
    setIsSending(true)
    axios
      .post(`/pods/api/agent/inbox/threads/${selectedId}/reply/`, {
        message: reply,
      })
      .then(({ data }) => {
        setThread(data)
        setReply("")
        loadThreads()
      })
      .catch((err) => setError(apiErrorMessage(err, "Could not send.")))
      .finally(() => setIsSending(false))
  }

  return (
    <Column gap={2} sx={{ pt: 2 }}>
      {error ? (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={scope}
        onChange={(_, value) => value && setScope(value)}
      >
        <ToggleButton value="mine">Mine</ToggleButton>
        <ToggleButton value="all">Whole pod</ToggleButton>
      </ToggleButtonGroup>
      <Paper
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: 480,
        }}
      >
        <List
          dense
          sx={{
            width: { xs: "100%", md: 340 },
            borderRight: { md: "1px solid" },
            borderColor: "divider",
            overflowY: "auto",
          }}
        >
          {threads.map((row) => (
            <ListItemButton
              key={row.thread_id}
              selected={row.thread_id === selectedId}
              onClick={() => setSelectedId(row.thread_id)}
            >
              <ListItemText
                primary={
                  <Row sx={{ justifyContent: "space-between", gap: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={row.unread_count ? 700 : 500}
                      noWrap
                    >
                      {row.subject}
                    </Typography>
                    <Badge
                      color="primary"
                      badgeContent={row.unread_count}
                      sx={{ mr: 1 }}
                    />
                  </Row>
                }
                secondary={`${clientAndCampaign(
                  row.client_name,
                  row.campaign_name
                )}${row.owner?.is_me ? "" : ` · ${row.owner?.name || ""}`} — ${
                  row.snippet || ""
                }`}
                secondaryTypographyProps={{ noWrap: true }}
              />
            </ListItemButton>
          ))}
          {threads.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No conversations yet.
            </Typography>
          ) : null}
        </List>
        <Column sx={{ flex: 1, p: 2, gap: 1.5 }}>
          {thread ? (
            <>
              <Column gap={0.25}>
                <Typography variant="h6" fontWeight={700}>
                  {thread.subject}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {clientAndCampaign(thread.client_name, thread.campaign_name)}{" "}
                  · from {thread.did_phone}
                  {thread.owner?.is_me ? "" : " · another agent's lead"}
                </Typography>
              </Column>
              <Column sx={{ gap: 1, flex: 1, overflowY: "auto" }}>
                {(thread.messages || []).map((message) => (
                  <Paper
                    key={message.id}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      maxWidth: "80%",
                      alignSelf: message.is_inbound ? "flex-start" : "flex-end",
                      bgcolor: message.is_inbound
                        ? "background.paper"
                        : "action.hover",
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {message.text}
                    </Typography>
                  </Paper>
                ))}
              </Column>
              <Row sx={{ gap: 1, alignItems: "flex-end" }}>
                <TextField
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Type a reply…"
                  multiline
                  minRows={2}
                  fullWidth
                  inputProps={{ "aria-label": "Reply" }}
                />
                <LoadingButton
                  variant="contained"
                  loading={isSending}
                  disabled={!reply.trim()}
                  onClick={sendReply}
                >
                  Send
                </LoadingButton>
              </Row>
            </>
          ) : (
            <Typography color="text.secondary">Pick a conversation.</Typography>
          )}
        </Column>
      </Paper>
    </Column>
  )
}
